import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  UserCircle, 
  Save, 
  Fingerprint, 
  Hash, 
  Building, 
  Activity,
  Loader2
} from "lucide-react";
import { useMutation, useQuery, gql } from "@apollo/client";
import { toast } from "@/components/ui/toast";

const UPDATE_PARTICIPANT = gql`
  mutation UpdateParticipantByODRNumber($odrnumber: Int!, $participant: Int, $updates: UpdateParticipantInput!) {
    updateParticipantByODRNumber(odrnumber: $odrnumber, participant: $participant, updates: $updates) {
      message
    }
  }
`;

const GET_HEALTHPLAN_PROVIDERS = gql`
  query GetHealthPlanProviders {
    getHealthPlanProviders {
      providerId
      providerName
    }
  }
`;

interface ParticipantInfoPanelProps {
  participantData: any;
  odrNumber: string;
  refetch: () => void;
}

export const ParticipantInfoPanel: React.FC<ParticipantInfoPanelProps> = ({ 
  participantData: data, 
  odrNumber,
  refetch 
}) => {
  const [formData, setFormData] = useState({
    ssn: "",
    cin: "",
    mrun: "",
    fin: "",
    cii: "",
    healthplan: "",
    provider: "", // PCP Name?
  });

  const { data: healthPlansData, loading: healthPlansLoading } = useQuery(GET_HEALTHPLAN_PROVIDERS);
  const [updateParticipant, { loading: updating }] = useMutation(UPDATE_PARTICIPANT);

  useEffect(() => {
    if (data) {
      setFormData({
        ssn: data.ssn || "",
        cin: data.cin || "",
        mrun: data.mrun || "",
        fin: data.fin || "",
        cii: data.cii || "",
        // Assuming healthplan is an object with healthplan name or id
        healthplan: data.healthplan?.healthplan || "", 
        provider: data.provider || "", 
      });
    }
  }, [data]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const updates: any = {
        ssn: formData.ssn,
        cin: formData.cin,
        mrun: formData.mrun,
        fin: formData.fin,
        cii: formData.cii,
        healthplan: formData.healthplan,
        provider: formData.provider,
      };

      const response = await updateParticipant({
        variables: {
          odrnumber: parseInt(odrNumber),
          updates: updates,
        },
      });

      // Check for success in response
      if (response.data?.updateParticipantByODRNumber?.message === "SUCCESS" || 
          response.data?.updateParticipantByODRNumber?.message?.toLowerCase().includes("success")) {
        toast.success("Participant updated successfully");
        refetch();
      } else {
        toast.success("Participant updated"); // Fallback success
        refetch();
      }
    } catch (error: any) {
      console.error("Update error:", error);
      toast.error("Failed to update participant");
    }
  };

  return (
    <Card className="shadow-sm">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <UserCircle className="h-5 w-5 text-blue-600" />
          <CardTitle className="text-base font-medium text-gray-800">
            Participant Information
          </CardTitle>
        </div>
        <div className="flex gap-2">
            <Button variant="default" size="sm" onClick={handleSave} disabled={updating}>
              {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
              Save
            </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          {/* Name - Read Only */}
          <div className="space-y-1">
            <span className="text-xs font-medium text-gray-500 uppercase">Name</span>
            <div className="font-medium text-gray-900 h-10 flex items-center">
              {data?.name?.firstName} {data?.name?.lastName}
            </div>
          </div>

          {/* SSN */}
          <div className="space-y-1">
            <span className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
              <Fingerprint className="h-3 w-3" /> SSN
            </span>
            <Input 
              aria-label="SSN"
              name="ssn" 
              value={formData.ssn} 
              onChange={handleInputChange} 
              className="h-8"
              placeholder="XXX-XX-XXXX"
            />
          </div>

          {/* CIN */}
          <div className="space-y-1">
            <span className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
              <Hash className="h-3 w-3" /> CIN
            </span>
            <Input 
              aria-label="CIN"
              name="cin" 
              value={formData.cin} 
              onChange={handleInputChange} 
              className="h-8"
            />
          </div>

          {/* MRUN */}
          <div className="space-y-1">
            <span className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
              <Hash className="h-3 w-3" /> MRUN
            </span>
            <Input 
              aria-label="MRUN"
              name="mrun" 
              value={formData.mrun} 
              onChange={handleInputChange} 
              className="h-8"
            />
          </div>

          {/* FIN */}
          <div className="space-y-1">
            <span className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
              <Hash className="h-3 w-3" /> FIN
            </span>
            <Input 
              aria-label="FIN"
              name="fin" 
              value={formData.fin} 
              onChange={handleInputChange} 
              className="h-8"
            />
          </div>

          {/* CII */}
          <div className="space-y-1">
            <span className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
              <Hash className="h-3 w-3" /> CII
            </span>
            <Input 
              aria-label="CII"
              name="cii" 
              value={formData.cii} 
              onChange={handleInputChange} 
              className="h-8"
            />
          </div>

          {/* Health Plan */}
          <div className="space-y-1">
            <span className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
              <Building className="h-3 w-3" /> Health Plan
            </span>
            <Select 
                value={formData.healthplan} 
                onValueChange={(value) => {
                   handleSelectChange("healthplan", value)
                }}
              >
                <SelectTrigger aria-label="Health Plan" className="h-8">
                  <SelectValue placeholder="Select Plan" />
                </SelectTrigger>
                <SelectContent>
                  {healthPlansLoading ? (
                    <SelectItem value="loading" disabled>Loading...</SelectItem>
                  ) : (
                    healthPlansData?.getHealthPlanProviders?.map((hp: any) => (
                      <SelectItem key={hp.providerName} value={hp.providerName}>
                        {hp.providerName}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
          </div>

          {/* Provider (PCP) */}
          <div className="space-y-1">
            <span className="text-xs font-medium text-gray-500 uppercase flex items-center gap-1">
              <Activity className="h-3 w-3" /> Provider (PCP)
            </span>
            <Input 
              aria-label="Provider"
              name="provider" 
              value={formData.provider} 
              onChange={handleInputChange} 
              className="h-8"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
